import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Textarea,
  Input,
  Label,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@tonic/shared/components/ui';
import {
  ArrowLeft,
  Calendar,
  Package,
  User,
  Clock,
  DollarSign,
  FileText,
  MoreVertical,
  Send,
  Phone,
  Mail,
  MapPin,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';

const mockRentalOrder = {
  id: "RO00123456",
  status: "Active",
  customer: {
    id: "CUST789",
    name: "Emily Johnson",
    email: "emily.j@email.com",
    phone: "+1 (555) 987-6543",
    address: "456 Oak Lane, Anytown, ST 12345",
    membershipLevel: "IKEA Family",
    rentalHistory: 3
  },
  items: [
    {
      id: "RENT001",
      name: "MALM Bed Frame",
      sku: "MALM-90160",
      condition: "Excellent",
      dailyRate: 15.99,
      deposit: 150.00
    },
    {
      id: "RENT002",
      name: "HEMNES Dresser",
      sku: "HEMNES-80360",
      condition: "Good",
      dailyRate: 12.99,
      deposit: 100.00
    }
  ],
  rental: {
    startDate: "2024-03-15",
    endDate: "2024-04-15",
    duration: "1 month",
    pickupTime: "10:00 AM",
    returnTime: "5:00 PM",
    location: "Main Store - Furniture Pickup Area",
    deliveryOption: "Self Pickup",
    specialInstructions: "Please bring valid ID and proof of address"
  },
  pricing: {
    itemsTotal: 428.98,
    delivery: 0,
    deposit: 250.00,
    insurance: 29.99,
    total: 458.97
  },
  insurance: {
    type: "Standard Coverage",
    provider: "IKEA Rental Protection",
    coverageDetails: [
      "Accidental damage",
      "Theft protection",
      "24/7 support"
    ],
    cost: 29.99,
    policyNumber: "INS-123456"
  },
  timeline: [
    {
      id: "EVENT001",
      type: "order_created",
      description: "Rental order created",
      timestamp: "2024-03-10T14:30:00Z",
      actor: "Emily Johnson"
    },
    {
      id: "EVENT002",
      type: "payment_confirmed",
      description: "Payment and deposit confirmed",
      timestamp: "2024-03-10T14:35:00Z",
      actor: "System"
    },
    {
      id: "EVENT003",
      type: "pickup_scheduled",
      description: "Pickup scheduled for March 15",
      timestamp: "2024-03-10T14:40:00Z",
      actor: "System"
    }
  ],
  notes: [
    {
      id: "NOTE001",
      author: "Sarah Brown",
      timestamp: "2024-03-10T15:00:00Z",
      content: "Customer requested early morning pickup",
      type: "internal"
    }
  ],
  documents: [
    {
      id: "DOC001",
      name: "Rental Agreement",
      type: "contract",
      uploadedAt: "2024-03-10T14:45:00Z"
    },
    {
      id: "DOC002",
      name: "Insurance Policy",
      type: "insurance",
      uploadedAt: "2024-03-10T14:45:00Z"
    }
  ]
};

function getStatusColor(status) {
  switch (status.toLowerCase()) {
    case 'active':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'completed':
      return 'outline';
    case 'overdue':
      return 'destructive';
    default:
      return 'outline';
  }
}

function getConditionColor(condition) {
  switch (condition.toLowerCase()) {
    case 'excellent':
      return 'default';
    case 'good':
      return 'secondary';
    case 'fair':
      return 'outline';
    case 'poor':
      return 'destructive';
    default:
      return 'outline';
  }
}

export function RentalOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('details');
  const [newNote, setNewNote] = React.useState('');

  // In a real app, we would fetch the rental order details using the id
  const order = mockRentalOrder;

  const handleBackClick = () => {
    navigate('/admin/re-commerce/rental/orders');
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      console.log("Adding note:", newNote);
      setNewNote('');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBackClick}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Rental Order #{order.id}</h1>
            <p className="text-sm text-muted-foreground">
              {order.items.length} items • {order.rental.duration}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={getStatusColor(order.status)}>
            {order.status}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Update Status</DropdownMenuItem>
              <DropdownMenuItem>Extend Rental</DropdownMenuItem>
              <DropdownMenuItem>Process Return</DropdownMenuItem>
              <DropdownMenuItem>Send Reminder</DropdownMenuItem>
              <DropdownMenuItem>Print Order</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="customer">Customer</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Rental Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date</Label>
                    <p className="font-medium">{new Date(order.rental.startDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <p className="font-medium">{new Date(order.rental.endDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <Label>Pickup Time</Label>
                    <p className="font-medium">{order.rental.pickupTime}</p>
                  </div>
                  <div>
                    <Label>Return Time</Label>
                    <p className="font-medium">{order.rental.returnTime}</p>
                  </div>
                  <div>
                    <Label>Delivery Option</Label>
                    <p className="font-medium">{order.rental.deliveryOption}</p>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <p className="font-medium">{order.rental.location}</p>
                  </div>
                </div>
                {order.rental.specialInstructions && (
                  <div>
                    <Label>Special Instructions</Label>
                    <p className="mt-1">{order.rental.specialInstructions}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pricing Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Items Total</span>
                    <span className="font-medium">${order.pricing.itemsTotal}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Delivery</span>
                    <span className="font-medium">${order.pricing.delivery}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Insurance</span>
                    <span className="font-medium">${order.pricing.insurance}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Security Deposit</span>
                    <span className="font-medium">${order.pricing.deposit}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-medium">Total</span>
                    <span className="font-medium">${order.pricing.total}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Insurance Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <p className="font-medium">{order.insurance.type}</p>
                </div>
                <div>
                  <Label>Provider</Label>
                  <p className="font-medium">{order.insurance.provider}</p>
                </div>
                <div>
                  <Label>Policy Number</Label>
                  <p className="font-medium">{order.insurance.policyNumber}</p>
                </div>
                <div>
                  <Label>Cost</Label>
                  <p className="font-medium">${order.insurance.cost}</p>
                </div>
              </div>
              <div>
                <Label>Coverage Details</Label>
                <ul className="mt-1 space-y-1 list-disc list-inside">
                  {order.insurance.coverageDetails.map((detail, index) => (
                    <li key={index}>{detail}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="items" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Rented Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.items.map((item) => (
                  <div key={item.id} className="flex items-start justify-between p-4 border rounded-lg">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-neutral-100 rounded-lg flex items-center justify-center">
                        <Package className="w-8 h-8 text-neutral-400" />
                      </div>
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-muted-foreground">SKU: {item.sku}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={getConditionColor(item.condition)}>
                            {item.condition}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            ${item.dailyRate}/day
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">Deposit: ${item.deposit}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customer" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <p className="font-medium">{order.customer.name}</p>
                </div>
                <div>
                  <Label>Membership</Label>
                  <p className="font-medium">{order.customer.membershipLevel}</p>
                </div>
                <div>
                  <Label>Email</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{order.customer.email}</p>
                    <Button variant="outline" size="sm">
                      <Mail className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Phone</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{order.customer.phone}</p>
                    <Button variant="outline" size="sm">
                      <Phone className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{order.customer.address}</p>
                    <Button variant="outline" size="sm">
                      <MapPin className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div>
                <Label>Rental History</Label>
                <p className="font-medium">{order.customer.rentalHistory} previous rentals</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
                <Button onClick={handleAddNote}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4">
                {order.notes.map((note) => (
                  <div key={note.id} className="flex items-start gap-4">
                    <div className="w-2 h-2 mt-2 rounded-full bg-blue-500" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{note.author}</span>
                        <span className="text-sm text-muted-foreground">
                          {new Date(note.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-1">{note.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Order Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.timeline.map((event) => (
                  <div key={event.id} className="flex items-start gap-4">
                    <div className="w-2 h-2 mt-2 rounded-full bg-blue-500" />
                    <div className="flex-1">
                      <p className="font-medium">{event.description}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{event.actor}</span>
                        <span>•</span>
                        <span>{new Date(event.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(doc.uploadedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      View
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 
